import { Extension } from '@tiptap/core';

// Consultants paste tour copy from Word, Google Docs, supplier PDFs and
// websites. Every one of those carries the source's own typography (font
// family, point size, colour, background) as inline `style`/`class`
// attributes plus `<span>`/`<div>` wrappers. Tiptap keeps whatever the schema
// can hold, so the pasted run rendered in Calibri 14pt red next to XT's own
// text — worst of all in the customer preview.
//
// XT therefore treats every paste as "paste and match style": the *styling*
// is dropped so the text adopts XT's typography, while the *structure* the
// consultant actually meant — paragraphs, line breaks, bullet/numbered
// lists, links, bold/italic emphasis — survives. A literal ⌘⌥⇧V would also
// flatten lists, which would force a consultant to re-bullet every set of
// tour inclusions by hand.
//
// This cleans the PASTE path only. Content already stored in the DB (legacy
// CMS pages written in the v2 admin) still loads and round-trips through
// EXTENDED_EXTENSIONS untouched.

// Attributes that carry the source document's look rather than its meaning.
const PRESENTATION_ATTRS = [
  'style',
  'class',
  'color',
  'face',
  'size',
  'bgcolor',
  'align',
  'width',
  'height',
];

// Wrappers that exist only to hang styling off. Unwrapping keeps their text
// and children in place; `<div>` becomes a `<p>` instead so a Word paste of
// div-per-line doesn't collapse into one run.
const UNWRAP_TAGS = new Set(['span', 'font']);
const DIV_TAGS = new Set(['div', 'section', 'article']);

// Dropped outright — Word/Excel ship `<style>`, `<meta>` and conditional
// `<o:p>` office markup inside the text/html flavour of the clipboard.
const DROP_TAGS = new Set([
  'style',
  'meta',
  'link',
  'script',
  'title',
  'colgroup',
  'col',
]);

const isOfficeTag = (tagName: string): boolean =>
  tagName.includes(':') || tagName.startsWith('o-') || tagName.startsWith('w-');

// Replaces `element` with its own children, preserving order.
const unwrap = (element: Element): void => {
  const parent = element.parentNode;
  if (!parent) return;
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
};

// Swaps `element` for a `<newTag>` carrying the same children.
const rename = (doc: Document, element: Element, newTag: string): void => {
  const replacement = doc.createElement(newTag);
  while (element.firstChild) {
    replacement.appendChild(element.firstChild);
  }
  element.replaceWith(replacement);
};

// Strips presentation from pasted HTML, keeping structural markup.
// Exported for the unit tests — the extension below is a thin wrapper.
export const stripPastedFormatting = (html: string): string => {
  if (!html.trim()) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');

  for (const element of Array.from(doc.body.querySelectorAll('*'))) {
    // querySelectorAll snapshots up-front, so an element may already have
    // been removed with its unwrapped ancestor by the time we reach it.
    if (!element.isConnected) continue;

    const tag = element.tagName.toLowerCase();

    if (DROP_TAGS.has(tag)) {
      element.remove();
      continue;
    }

    if (isOfficeTag(tag)) {
      unwrap(element);
      continue;
    }

    for (const attr of PRESENTATION_ATTRS) {
      element.removeAttribute(attr);
    }
    // Word/Docs also tag runs with `lang`, `dir` and `data-*` bookkeeping.
    for (const attr of Array.from(element.attributes)) {
      if (attr.name.startsWith('data-') || attr.name.startsWith('aria-')) {
        element.removeAttribute(attr.name);
      }
    }

    if (UNWRAP_TAGS.has(tag)) {
      unwrap(element);
    } else if (DIV_TAGS.has(tag)) {
      rename(doc, element, 'p');
    }
  }

  return doc.body.innerHTML;
};

export const PastePlain = Extension.create({
  name: 'pastePlain',

  transformPastedHTML(html) {
    return stripPastedFormatting(html);
  },
});
